import { ORDER_KPIS, type OrderKpiKey, type OrderKpiRange, type OrderKpiValue } from "@logiful/domain";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { CalendarDays, Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTenant } from "@/components/AppShell";
import { Card, cx, Skeleton, Tip } from "@/components/ui";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { intlLocale, msg, t } from "@/lib/i18n";

const RANGES: { key: OrderKpiRange; label: string }[] = [
  { key: "today", label: msg("Heute") },
  { key: "7d", label: msg("Letzte 7 Tage") },
  { key: "30d", label: msg("Letzte 30 Tage") },
];
const STORE = "logiful.orders.kpiRange";

/** Bezeichnung und Erklärung je Kennzahl (auch für die Auswahl bei Logiful intern) */
export const KPI_TEXT: Record<OrderKpiKey, { label: string; hint: string }> = {
  orders: { label: msg("Aufträge"), hint: msg("Eingegangene Bestellungen im Zeitraum – je Shop-Bestellung einmal, auch wenn sie im Shop neu angefordert oder aus mehreren Standorten geliefert wird (ohne Testaufträge und Restlieferungen)") },
  items: { label: msg("Artikel bestellt"), hint: msg("Bestellte Stückzahl im Zeitraum – ersetzte Anfragen zählen nicht doppelt (ohne Beilagen aus Regeln)") },
  value: { label: msg("Warenwert"), hint: msg("Summe der Bestellwerte im Zeitraum – je Shop-Bestellung einmal") },
  shipped: { label: msg("Versendet"), hint: msg("Aufträge, deren erstes Paket im Zeitraum das Lager verlassen hat") },
  delivered: { label: msg("Zugestellt"), hint: msg("Aufträge, die laut Sendungsverfolgung im Zeitraum zugestellt wurden") },
  on_hold: { label: msg("Zurückgehalten"), hint: msg("Aufträge, die gerade mit Grund warten (ohne Testaufträge) – Klick zeigt sie") },
  on_time: { label: msg("Pünktlich versendet"), hint: msg("Anteil der im Zeitraum versendeten Aufträge, die bis zur Versandfrist raus waren") },
  handover_to_ship: { label: msg("Übergabe bis Versand"), hint: msg("Durchschnittliche Zeit von der Übergabe an Logiful bis zum Versand") },
  ship_to_delivery: { label: msg("Versand bis Zustellung"), hint: msg("Durchschnittliche Laufzeit beim Versanddienstleister vom Versand bis zur Zustellung") },
};

function readRange(): OrderKpiRange {
  try {
    const v = localStorage.getItem(STORE);
    return v === "7d" || v === "30d" ? v : "today";
  } catch {
    return "today";
  }
}

/** Wert passend zur Einheit; null = keine Grundlage */
export function fmtKpi(key: OrderKpiKey, v: number | null): string {
  if (v == null) return "—";
  const n = (x: number, d = 0) => x.toLocaleString(intlLocale(), { maximumFractionDigits: d, minimumFractionDigits: d });
  switch (ORDER_KPIS[key].unit) {
    case "money":
      return fmtMoney(Math.round(v));
    case "percent":
      return `${n(v, 1)} %`;
    case "hours":
      return `${n(v, 1)} h`;
    case "days":
      return t("{n} Tage", { n: n(v, 1) });
    default:
      return n(v);
  }
}

/** kleiner Verlauf wie bei Shopify – Lücken (keine Grundlage) werden übersprungen */
function Sparkline({ series }: { series: (number | null)[] }) {
  const W = 72;
  const Hh = 22;
  const pts = series.map((v, i) => ({ i, v }));
  const vals = pts.filter((p): p is { i: number; v: number } => p.v != null);
  if (series.length < 2 || !vals.length) return <span className="w-[72px]" />;
  const max = Math.max(...vals.map((p) => p.v));
  const min = Math.min(0, ...vals.map((p) => p.v));
  const x = (i: number) => (i / (series.length - 1)) * (W - 2) + 1;
  const y = (v: number) => (max === min ? Hh - 2 : Hh - 2 - ((v - min) / (max - min)) * (Hh - 5));
  const d = vals.map((p, k) => `${k ? "L" : "M"}${x(p.i).toFixed(1)} ${y(p.v).toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={Hh} viewBox={`0 0 ${W} ${Hh}`} className="text-info shrink-0" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Kennzahlen-Leiste über der Auftragsliste (Shopify-Stil): Zeitraum links, je Kachel Wert und Verlauf.
 * Welche Kacheln erscheinen, legt Logiful zentral fest – der Server liefert nur diese.
 */
export function OrderKpiStrip({ onSelect }: { onSelect?: (key: OrderKpiKey) => void }) {
  const { tenantId } = useTenant();
  const [range, setRange] = useState<OrderKpiRange>(readRange);
  const q = useQuery({ queryKey: ["order-kpis", tenantId, range], queryFn: () => api.orders.kpis(tenantId, range), placeholderData: keepPreviousData, refetchInterval: 60_000 });
  const choose = (r: OrderKpiRange) => {
    setRange(r);
    try { localStorage.setItem(STORE, r); } catch { /* ohne Speicher: nur für diese Sitzung */ }
  };
  if (q.data && !q.data.items.length) return null;
  const items: (OrderKpiValue | null)[] = q.data?.items ?? [null, null, null, null];
  return (
    <Card className="flex items-stretch overflow-x-auto">
      {/* eigenes Menü statt nativer Auswahl – die ist im dunklen Modus je nach Browser kaum lesbar */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button type="button" aria-label={t("Zeitraum der Kennzahlen")} className="shrink-0 flex items-center gap-1.5 px-4 text-[13px] font-medium hover:bg-surface-2/60 data-[state=open]:bg-surface-2/60 rounded-l-xl outline-none focus-visible:ring-2 focus-visible:ring-ink/20">
            <CalendarDays className="size-4 text-fg-muted" />
            {t(RANGES.find((r) => r.key === range)!.label)}
            <ChevronDown className="size-3.5 text-fg-subtle" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="start" sideOffset={6} className="z-50 w-48 rounded-xl border border-border bg-surface p-1.5 shadow-xl animate-pop-in">
            {RANGES.map((r) => (
              <DropdownMenu.Item key={r.key} onSelect={() => choose(r.key)} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-[13px] text-fg outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                {t(r.label)}
                {r.key === range && <Check className="size-3.5 text-fg-muted" />}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {items.map((k, i) => {
        if (!k) return <div key={i} className="flex-1 min-w-[150px] border-l border-border px-4 py-2.5"><Skeleton className="h-3 w-20" /><Skeleton className="h-4 w-10 mt-2" /></div>;
        const def = ORDER_KPIS[k.key];
        const text = KPI_TEXT[k.key];
        const clickable = !!onSelect && k.key === "on_hold";
        const Cell = clickable ? "button" : "div";
        return (
          <Cell key={k.key} {...(clickable ? { type: "button" as const, onClick: () => onSelect!(k.key) } : {})} className={cx("flex-1 min-w-[150px] border-l border-border px-4 py-2.5 text-left", clickable && "hover:bg-surface-2/60")}>
            <Tip content={t(text.hint)}>
              <span className="text-[12.5px] font-medium underline decoration-dotted decoration-fg-subtle underline-offset-[3px] whitespace-nowrap">{t(text.label)}</span>
            </Tip>
            <div className="mt-1 flex items-end justify-between gap-3">
              <span className={cx("text-[15px] font-semibold tabular-nums whitespace-nowrap", q.isPlaceholderData && "opacity-60")}>{fmtKpi(k.key, k.value)}</span>
              {!def.snapshot && <Sparkline series={k.series} />}
            </div>
          </Cell>
        );
      })}
    </Card>
  );
}
